import React, { useState, useEffect } from 'react';
import { screensService, displayService, groupsService } from '../services/api';
import axios from 'axios';

function ScreenCard({ screen, onRefresh, detailed = false }) {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [loadingScreenshot, setLoadingScreenshot] = useState(true);
  const [displayPages, setDisplayPages] = useState([]);
  const [defaultPageId, setDefaultPageId] = useState(screen.default_page_id || '');
  const [groups, setGroups] = useState([]);
  const [editedName, setEditedName] = useState(screen.name || '');
  const [groupId, setGroupId] = useState(screen.group_id || '');

  const [schedules, setSchedules] = useState([]);
  const [newSchedTime, setNewSchedTime] = useState('06:00');
  const [newSchedPageId, setNewSchedPageId] = useState('');

  useEffect(() => {
    displayService.getAll().then(r => setDisplayPages(r.data)).catch(() => {});
    groupsService.getAll().then(r => setGroups(r.data)).catch(() => {});
    loadSchedules();
  }, []);

  const loadSchedules = async () => {
    try {
      const r = await axios.get('/api/screen-schedules?screen_id=' + screen.id);
      setSchedules(r.data);
    } catch (e) {}
  };

  const addSchedule = async () => {
    if (!newSchedPageId) return;
    try {
      await axios.post('/api/screen-schedules', {
        screen_id: screen.id,
        display_page_id: parseInt(newSchedPageId),
        start_time: newSchedTime
      });
      setMessage('Planning ajoute');
      setTimeout(() => setMessage(''), 2000);
      loadSchedules();
    } catch (e) {
      setMessage('Erreur planning');
    }
  };

  const deleteSchedule = async (id) => {
    try {
      await axios.delete('/api/screen-schedules/' + id);
      loadSchedules();
    } catch (e) {}
  };

  const handleSaveName = async () => {
    if (!editedName.trim() || editedName === screen.name) return;
    try {
      await screensService.update(screen.id, { name: editedName.trim() });
      setMessage('Nom enregistre');
      setTimeout(() => setMessage(''), 2000);
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage('Erreur renommage');
    }
  };

  const handleSaveGroup = async (value) => {
    setGroupId(value);
    try {
      await screensService.update(screen.id, { group_id: value ? parseInt(value) : null });
      setMessage('Groupe enregistre');
      setTimeout(() => setMessage(''), 2000);
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage('Erreur groupe');
    }
  };

  const handleSaveDefaultPage = async (value) => {
    setDefaultPageId(value);
    try {
      await screensService.update(screen.id, { default_page_id: value ? parseInt(value) : null });
      setMessage('Page native enregistree');
      setTimeout(() => setMessage(''), 2000);
      if (onRefresh) onRefresh();
    } catch (e) {
      setMessage('Erreur enregistrement');
    }
  };

  useEffect(() => {
    loadLatestScreenshot();
    const interval = setInterval(loadLatestScreenshot, 30000);
    return () => clearInterval(interval);
  }, [screen.id]);

  const loadLatestScreenshot = async () => {
    try {
      const response = await axios.get(`/api/screenshots?screen_id=${screen.id}`);
      if (response.data && response.data.length > 0) {
        setScreenshot(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading screenshot:', error);
    } finally {
      setLoadingScreenshot(false);
    }
  };

  const requestScreenshot = async () => {
    setLoading(true);
    try {
      await screensService.requestScreenshot(screen.id);
      setMessage('Screenshot demande');
      setTimeout(() => {
        setMessage('');
        loadLatestScreenshot();
      }, 5000);
    } catch (error) {
      setMessage('Erreur screenshot');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const sendCommand = async (command, params = {}) => {
    setLoading(true);
    setMessage('');
    
    try {
      await screensService.sendCommand(screen.id, command, params);
      setMessage('Commande envoyee');
      setTimeout(() => setMessage(''), 3000);
      if (onRefresh) onRefresh();
    } catch (error) {
      setMessage('Erreur');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeURL = () => {
    if (url.trim()) {
      sendCommand('change_url', { url });
      setUrl('');
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className={`screen-card ${screen.is_online ? 'online' : 'offline'}`}>
      <div className="screen-header">
        <h3>{screen.name}</h3>
        <span className={`status-badge ${screen.is_online ? 'online' : 'offline'}`}>
          {screen.is_online ? 'En ligne' : 'Hors ligne'}
        </span>
      </div>

      {message && <div className="message-banner">{message}</div>}

      {screen.override_page_id && (
        <div style={{
          padding: '8px 12px',
          margin: '8px 0',
          background: 'rgba(255,150,50,0.15)',
          border: '1px solid rgba(255,150,50,0.4)',
          borderRadius: '4px',
          fontSize: '0.8rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>
            <strong style={{color: '#ffb84d'}}>OVERRIDE</strong>
            {screen.override_until ? (
              "jusqu au " + new Date(screen.override_until).toLocaleString('fr-CH', {hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit'})
            ) : (
              ' - permanent'
            )}
          </span>
          <button
            onClick={async () => {
              try {
                await screensService.clearOverride(screen.id);
                if (onRefresh) onRefresh();
              } catch (e) {
                setMessage('Erreur');
              }
            }}
            className="btn-sm"
            style={{padding: '2px 8px', fontSize: '0.7rem'}}
          >
            Reprendre le planning
          </button>
        </div>
      )}

      {detailed && (
        <div className="screen-main-content">
          {/* Colonne gauche : Screenshot */}
          <div className="screenshot-column">
            {loadingScreenshot ? (
              <div className="screenshot-thumbnail loading">
                <p>...</p>
              </div>
            ) : screenshot ? (
              <div 
                className="screenshot-thumbnail"
                onClick={() => window.open(`/uploads/screenshots/${screenshot.filename}`, '_blank')}
                title="Cliquer pour agrandir"
              >
                <img 
                  src={`/uploads/screenshots/${screenshot.filename}`}
                  alt={`Screenshot ${screen.name}`}
                />
                <div className="screenshot-overlay">
                  <span>Voir</span>
                </div>
              </div>
            ) : (
              <div className="screenshot-thumbnail empty">
                <p>Pas de screenshot</p>
              </div>
            )}
            <div className="screenshot-meta">
              {screenshot && <small>{formatDate(screenshot.created_at)}</small>}
              {screen.is_online && (
                <button 
                  onClick={requestScreenshot} 
                  disabled={loading}
                  className="btn-screenshot-mini"
                  title="Capturer nouveau screenshot"
                >
                  Refresh
                </button>
              )}
            </div>
          </div>

          {/* Colonne droite : Infos et controles */}
          <div className="info-controls-column">
            <div className="screen-info">
              <div className="info-row">
                <span className="label">Pi ID:</span>
                <span className="value">{screen.pi_id}</span>
              </div>
              {screen.group_name && (
                <div className="info-row">
                  <span className="label">Groupe:</span>
                  <span className="value">{screen.group_name}</span>
                </div>
              )}
              {screen.cpu_temp && (
                <div className="info-row">
                  <span className="label">Temperature:</span>
                  <span className="value">{screen.cpu_temp.toFixed(1)}C</span>
                </div>
              )}
              {screen.uptime && (
                <div className="info-row">
                  <span className="label">Uptime:</span>
                  <span className="value">{formatUptime(screen.uptime)}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">TV:</span>
                <span className={`value tv-${screen.tv_power}`}>
                  {screen.tv_power === 'on' ? 'Allumee' : 
                   screen.tv_power === 'off' ? 'Eteinte' : 'Inconnu'}
                </span>
              </div>
            </div>

            {screen.is_online && (
              <>
                <div className="control-section">
                  <h4>Changer URL</h4>
                  <div className="url-control">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="https://example.com"
                    />
                    <button onClick={handleChangeURL} disabled={loading}>
                      Mettre a jour
                    </button>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Controle TV</h4>
                  <div className="button-group">
                    <button 
                      onClick={() => sendCommand('tv_control', { action: 'on' })}
                      disabled={loading}
                      className="btn-success"
                    >
                      Allumer
                    </button>
                    <button 
                      onClick={() => sendCommand('tv_control', { action: 'off' })}
                      disabled={loading}
                      className="btn-danger"
                    >
                      Eteindre
                    </button>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Page d'affichage</h4>
                  <div className="button-group">
                    <button
                      onClick={() => sendCommand('change_url', { url: 'https://dynaview.dynatime.ch/display/' + screen.pi_id })}
                      disabled={loading}
                      className="btn-info"
                    >
                      Display Page
                    </button>
                    <button
                      onClick={() => sendCommand('change_url', { url: 'https://dynaview.dynatime.ch/display/' + screen.pi_id + '/identify' })}
                      disabled={loading}
                      className="btn-warning"
                    >
                      Identifier
                    </button>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Identification</h4>
                  <div className="form-group">
                    <label>Nom</label>
                    <div className="url-control">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        placeholder="Nom de l'ecran"
                      />
                      <button
                        onClick={handleSaveName}
                        className="btn-primary btn-sm"
                        disabled={!editedName.trim() || editedName === screen.name}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                  <div className="form-group" style={{marginTop: 12, marginBottom: 0}}>
                    <label>Groupe</label>
                    <select
                      value={groupId}
                      onChange={(e) => handleSaveGroup(e.target.value)}
                    >
                      <option value="">-- Aucun --</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Page native (au demarrage)</h4>
                  <select
                    value={defaultPageId}
                    onChange={(e) => handleSaveDefaultPage(e.target.value)}
                    style={{width: '100%', padding: '8px', background: '#2a2a3e', color: '#fff', border: '1px solid #444', borderRadius: '4px'}}
                  >
                    <option value="">-- Aucune (auto) --</option>
                    {displayPages.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>

                <div className="control-section">
                  <h4>Planning horaire</h4>
                  {schedules.length > 0 && (
                    <div className="schedule-list">
                      {schedules.map(s => (
                        <div key={s.id} className="schedule-row">
                          <span className="schedule-time">{s.start_time}</span>
                          <span className="schedule-page">{s.display_page_name}</span>
                          <button 
                            onClick={() => deleteSchedule(s.id)}
                            className="btn-delete-small"
                            title="Supprimer"
                          >x</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="schedule-add">
                    <input
                      type="time"
                      value={newSchedTime}
                      onChange={(e) => setNewSchedTime(e.target.value)}
                      style={{width: '90px', padding: '6px', background: '#2a2a3e', color: '#fff', border: '1px solid #444', borderRadius: '4px'}}
                    />
                    <select
                      value={newSchedPageId}
                      onChange={(e) => setNewSchedPageId(e.target.value)}
                      style={{flex: 1, padding: '6px', background: '#2a2a3e', color: '#fff', border: '1px solid #444', borderRadius: '4px'}}
                    >
                      <option value="">-- Page --</option>
                      {displayPages.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button onClick={addSchedule} className="btn-primary btn-sm" disabled={!newSchedPageId}>+</button>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Systeme</h4>
                  <button 
                    onClick={() => {
                      if (window.confirm('Confirmer le redemarrage ?')) {
                        sendCommand('reboot');
                      }
                    }}
                    disabled={loading}
                    className="btn-warning"
                  >
                    Redemarrer
                  </button>
                  <button 
                    onClick={() => {
                      if (window.confirm('Confirmer l extinction du Pi ?')) {
                        sendCommand('shutdown');
                      }
                    }}
                    disabled={loading}
                    className="btn-danger"
                    style={{marginLeft: '8px'}}
                  >
                    Eteindre le Pi
                  </button>

                </div>
              </>
            )}
          </div>
        </div>
      )}

      <button 
        onClick={async () => {
          if (window.confirm('Supprimer definitivement cet ecran du dashboard ?')) {
            try {
              await screensService.delete(screen.id);
              if (onRefresh) onRefresh();
            } catch (e) {
              alert('Erreur suppression');
            }
          }
        }}
        className="btn-danger btn-sm"
        style={{marginTop: '12px', padding: '4px 10px', fontSize: '0.7rem', opacity: 0.4, width: 'auto', alignSelf: 'flex-end'}}
      >
        Supprimer
      </button>
    </div>
  );
}

export default ScreenCard;
